import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { BookOpen, ChatCircleDots, SignOut, Megaphone, Article, Key, Newspaper, GoogleLogo, List, X } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useCustomer } from "@/context/CustomerContext";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import logoMark from "@/assets/lumina-mark.png";

const linkBase =
  "font-sans text-xs uppercase tracking-[0.18em] transition-colors";

const NAV_LINKS = [
  { to: "/chat", label: "Alter Ego", Icon: ChatCircleDots, testid: "alter-ego", active: "text-[#FF6B6B]" },
  { to: "/studio", label: "Ebook Studio", Icon: BookOpen, testid: "ebook-studio", active: "text-[#F59E0B]" },
  { to: "/promote", label: "Featured", Icon: Megaphone, testid: "promote", active: "text-[#F59E0B]" },
  { to: "/blog", label: "Blog", Icon: Article, testid: "blog", active: "text-[#F59E0B]" },
  { to: "/articles", label: "Articles", Icon: Newspaper, testid: "articles", active: "text-[#F59E0B]" },
];

const MobileMenu = ({ open, onClose, isAdmin, logout, customer, signIn, signOut }) => {
  if (!open) return null;
  const item = "flex items-center gap-3 px-6 py-4 text-sm uppercase tracking-[0.18em] border-b border-white/5 transition-colors";
  return (
    <div className="sm:hidden absolute inset-x-0 top-16 bg-[#0B0B0C] border-b border-white/10 shadow-2xl animate-in slide-in-from-top-2 duration-200" data-testid="mobile-menu">
      {NAV_LINKS.map(({ to, label, Icon, testid, active }) => (
        <NavLink
          key={to}
          to={to}
          onClick={onClose}
          data-testid={`mobile-nav-${testid}`}
          className={({ isActive }) => `${item} ${isActive ? active : "text-white/70"}`}
        >
          <Icon size={18} weight="light" /> {label}
        </NavLink>
      ))}
      {!isAdmin && (customer ? (
        <>
          <Link to="/studio/purchases" onClick={onClose} data-testid="mobile-nav-library" className={`${item} text-white/70`}>
            <BookOpen size={18} weight="light" /> My library
          </Link>
          <button onClick={() => { signOut(); onClose(); }} data-testid="mobile-nav-signout" className={`${item} w-full text-white/50`}>
            <SignOut size={18} weight="light" /> Sign out
          </button>
        </>
      ) : (
        <button onClick={() => { signIn(); onClose(); }} data-testid="mobile-nav-signin" className={`${item} w-full text-white/70`}>
          <GoogleLogo size={18} weight="bold" /> Sign in with Google
        </button>
      ))}
      {isAdmin ? (
        <button onClick={() => { logout(); onClose(); }} data-testid="mobile-nav-logout" className={`${item} w-full text-white/50`}>
          <SignOut size={18} weight="light" /> Admin logout
        </button>
      ) : (
        <NavLink to="/admin/login" onClick={onClose} data-testid="mobile-nav-admin" className={`${item} text-white/40`}>
          <Key size={18} weight="light" /> Admin
        </NavLink>
      )}
    </div>
  );
};

export const Navbar = () => {
  const { isAdmin, logout } = useAuth();
  const { customer, signIn, signOut } = useCustomer();
  const [pwOpen, setPwOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
      <nav className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
        <Link
          to="/"
          data-testid="logo-home-link"
          className="group flex items-center gap-2.5 text-white hover:text-white/80 transition-colors"
        >
          <img
            src={logoMark}
            alt="Lumina"
            className="h-8 w-8 object-contain transition-transform duration-300 group-hover:scale-110 drop-shadow-[0_0_8px_rgba(245,158,11,0.35)]"
          />
          <span className="font-serif text-2xl tracking-tight">
            Lumina<span className="text-[#F59E0B]">.</span>
          </span>
        </Link>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          className="sm:hidden text-white/80 hover:text-white p-2 -mr-2"
        >
          {menuOpen ? <X size={24} /> : <List size={24} />}
        </button>

        <div className="hidden sm:flex items-center gap-6 sm:gap-8">
          <NavLink
            to="/chat"
            data-testid="nav-alter-ego"
            className={({ isActive }) =>
              `${linkBase} flex items-center gap-1.5 ${
                isActive ? "text-[#FF6B6B]" : "text-white/60 hover:text-white"
              }`
            }
          >
            <ChatCircleDots size={15} weight="light" />
            <span className="hidden sm:inline">Alter Ego</span>
          </NavLink>
          <NavLink
            to="/studio"
            data-testid="nav-ebook-studio"
            className={({ isActive }) =>
              `${linkBase} flex items-center gap-1.5 ${
                isActive ? "text-[#F59E0B]" : "text-white/60 hover:text-white"
              }`
            }
          >
            <BookOpen size={15} weight="light" />
            <span className="hidden sm:inline">Ebook Studio</span>
          </NavLink>
          <NavLink
            to="/promote"
            data-testid="nav-promote"
            className={({ isActive }) =>
              `${linkBase} flex items-center gap-1.5 ${
                isActive ? "text-[#F59E0B]" : "text-white/60 hover:text-white"
              }`
            }
          >
            <Megaphone size={15} weight="light" />
            <span className="hidden sm:inline">Featured</span>
          </NavLink>
          <NavLink
            to="/blog"
            data-testid="nav-blog"
            className={({ isActive }) =>
              `${linkBase} flex items-center gap-1.5 ${
                isActive ? "text-[#F59E0B]" : "text-white/60 hover:text-white"
              }`
            }
          >
            <Article size={15} weight="light" />
            <span className="hidden sm:inline">Blog</span>
          </NavLink>
          <NavLink
            to="/articles"
            data-testid="nav-articles"
            className={({ isActive }) =>
              `${linkBase} flex items-center gap-1.5 ${
                isActive ? "text-[#F59E0B]" : "text-white/60 hover:text-white"
              }`
            }
          >
            <Newspaper size={15} weight="light" />
            <span className="hidden sm:inline">Articles</span>
          </NavLink>

          {!isAdmin && (customer ? (
            <div className="flex items-center gap-3">
              <Link
                to="/studio/purchases"
                data-testid="nav-customer-chip"
                title="My library"
                className="flex items-center gap-2 group"
              >
                {customer.picture ? (
                  <img
                    src={customer.picture}
                    alt={customer.name}
                    referrerPolicy="no-referrer"
                    className="h-7 w-7 rounded-full border border-[#F59E0B]/40 group-hover:border-[#F59E0B] transition-colors"
                  />
                ) : (
                  <span className="h-7 w-7 rounded-full bg-[#B45309] text-white text-xs flex items-center justify-center">
                    {(customer.name || customer.email || "?")[0].toUpperCase()}
                  </span>
                )}
                <span className={`${linkBase} hidden md:inline text-white/60 group-hover:text-white`}>
                  {(customer.name || customer.email).split(" ")[0]}
                </span>
              </Link>
              <button
                data-testid="nav-customer-signout"
                onClick={signOut}
                title="Sign out"
                className={`${linkBase} flex items-center text-white/40 hover:text-white`}
              >
                <SignOut size={15} weight="light" />
              </button>
            </div>
          ) : (
            <button
              data-testid="nav-google-signin"
              onClick={signIn}
              className={`${linkBase} flex items-center gap-1.5 text-white/60 hover:text-[#F59E0B]`}
            >
              <GoogleLogo size={15} weight="bold" />
              <span className="hidden sm:inline">Sign in</span>
            </button>
          ))}

          {isAdmin ? (
            <>
              <button
                data-testid="nav-change-password"
                onClick={() => setPwOpen(true)}
                title="Change password"
                className={`${linkBase} flex items-center gap-1.5 text-white/40 hover:text-white`}
              >
                <Key size={15} weight="light" />
                <span className="hidden sm:inline">Password</span>
              </button>
              <button
                data-testid="nav-logout"
                onClick={logout}
                className={`${linkBase} flex items-center gap-1.5 text-white/40 hover:text-white`}
              >
                <SignOut size={15} weight="light" />
                <span className="hidden sm:inline">Logout</span>
              </button>
              <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
            </>
          ) : (
            <NavLink
              to="/admin/login"
              data-testid="nav-admin-login"
              className={({ isActive }) =>
                `${linkBase} ${isActive ? "text-white" : "text-white/40 hover:text-white"}`
              }
            >
              Admin
            </NavLink>
          )}
        </div>
      </nav>
      <MobileMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        isAdmin={isAdmin}
        logout={logout}
        customer={customer}
        signIn={signIn}
        signOut={signOut}
      />
    </header>
  );
};
